<template>
  <div v-if="aulaAvulsa.id" class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
        <h4 class="form-title">Detalhes da Aula Avulsa nº {{ aulaAvulsa.id }} </h4>
        <button class="btn btn-secondary" @click="voltar">Voltar</button>
      </div>
      <p></p>
      <!-- Dados do Responsável -->
      <FormSection title="Dados do Responsável">
        <div class="info-group">
          <strong>Responsável:</strong> {{ aulaAvulsa.responsavel_nome || '-' }}
        </div>
      </FormSection>
  
      <!-- Dados do Aluno e Observações -->
      <FormSection title="Dados do Aluno e Observações">
        <div class="info-group">
          <strong>Aluno:</strong> {{ aulaAvulsa.aluno_nome || '-' }}
        </div>
        <div class="info-group">
          <strong>Observações:</strong> {{ aulaAvulsa.observacoes || '-' }}
        </div>
      </FormSection>
  
      <!-- Aulas Contratadas -->
      <FormSection title="Aulas Contratadas">
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead class="table-light">
              <tr>
                <th>Nível</th>
                <th>Turma</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="aula in aulasContratadas" :key="aula.id">
                <td>{{ aula.nivel || "Sem nível" }}</td>
                <td>{{ aula.turma_id }} | {{ aula.instrutor_nome }} | {{ aula.dia_semana }} | {{ globalUtils.formatarHora(aula.horario) }}</td>
                <td>{{ globalUtils.formatarData(aula.data_aula) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </FormSection>
  
      <!-- Dados Financeiros -->
      <FormSection title="Dados Financeiros">
          <div class="info-group">
            <strong>Valor unitário:</strong> {{ globalUtils.formatarValor(aulaAvulsa.valor || 0) }}
          </div>
          <div class="info-group">
            <strong>Desconto (%):</strong> {{ aulaAvulsa.desconto }}
          </div>
          <div class="info-group">
            <strong>Nº pagamentos:</strong> {{ aulaAvulsa.quantidade_pagamentos }}
          </div>
          <div class="info-group">
            <strong>Total a pagar:</strong> {{ globalUtils.formatarValor(aulaAvulsa.total_pagar || 0) }}
          </div>
      </FormSection>
  
      <!-- Formas de Pagamento -->
      <FormSection title="Formas de Pagamento">
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Forma de Pagamento</th>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Pago</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(pagamento, index) in pagamentos" :key="index">
                <td>{{ pagamento.forma }}</td>
                <td>{{ globalUtils.formatarData(pagamento.vencimento) }}</td>
                <td>{{ globalUtils.formatarValor(pagamento.valor) }}</td>
                <td>{{ pagamento.pago ? "Sim" : "Não" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </FormSection>
  
      <div class="totais">
        <strong>Total Pago:</strong> {{ globalUtils.formatarValor(totalPagamentos || 0) }}<br>
        <strong>Saldo Restante:</strong> 
        <span :class="saldoRestante > 0 ? 'text-danger' : 'text-success'">
          {{ globalUtils.formatarValor(Math.max(saldoRestante, 0)) }}
        </span>
      </div>
    </div>
 
  <div v-else>
    <LoadingSpinner />
  </div>

  </template>
    

  <script>
  import { reactive, ref, onMounted } from "vue";
  import { useRouter, useRoute } from "vue-router";
  import axios from "@/services/axios";
  import FormSection from "@/components/Layout/FormSection.vue";
  import globalUtils from "@/utils/globalUtils";
  
  export default {
    components: { FormSection },
    setup() {
      const router = useRouter();
      const route = useRoute();
      const aulaAvulsa = reactive({});
      const aulasContratadas = ref([]);
      const pagamentos = ref([]);
      const totalPagamentos = ref(0);
      const saldoRestante = ref(0);
  
      const carregarAulaAvulsa = async (id) => {
        try {
          const response = await axios.get(`/operacional/aulas-avulsas/${id}`);
          Object.assign(aulaAvulsa, response.data);
        } catch (error) {
          console.error("Erro ao carregar aula avulsa:", error);
        }
      };
  
      const carregarAulasContratadas = async (id) => {
        try {
          const response = await axios.get(`/operacional/aulas-avulsas-contratadas/${id}`);
          aulasContratadas.value = response.data;
        } catch (error) {
          console.error("Erro ao carregar aulas contratadas:", error);
        }
      };
  
      const carregarPagamentos = async (id) => {
        try {
            const response = await axios.get(`/operacional/aulas-avulsas-pagamentos/${id}`);
            
            // 🚀 Mapear os pagamentos e associar o nome da forma de pagamento corretamente
            pagamentos.value = response.data.map(p => ({
            ...p,
            forma: formasPagamento.value.find(f => f.id === p.forma_pagamento_id)?.nome || "Desconhecido"
            }));

            totalPagamentos.value = pagamentos.value.reduce((sum, p) => sum + parseFloat(p.valor || 0), 0);
            saldoRestante.value = parseFloat(aulaAvulsa.total_pagar || 0) - totalPagamentos.value;

        } catch (error) {
            console.error("Erro ao carregar pagamentos:", error);
        }
      };
  
      const formasPagamento = ref([]);

      const carregarFormasPagamento = async () => {
        try {
            const response = await axios.get("/cadastros/formas-pagamento");
            formasPagamento.value = response.data; // [{ id: 1, nome: "PIX" }, { id: 2, nome: "Dinheiro" }]
        } catch (error) {
            console.error("❌ Erro ao carregar formas de pagamento:", error);
        }
      };


      const voltar = () => {
        router.push("/operacional/aulas-avulsas");
      };
  
      onMounted(async () => {
        await carregarFormasPagamento(); // 🔹 Primeiro, carregamos as formas de pagamento
        if (route.params.id) {
          await carregarAulaAvulsa(route.params.id);
          await carregarAulasContratadas(route.params.id);
          await carregarPagamentos(route.params.id);
        }
      });
  
      return {
        aulaAvulsa,
        aulasContratadas,
        pagamentos,
        totalPagamentos,
        saldoRestante,
        globalUtils,
        voltar,
      };
    },
  };
  </script>
  
  <style scoped>
.detalhes-container {
  max-width: 90%;
  margin: auto;
  padding: 20px;
  background: white;
  border-radius: 8px;
}
.header {
  margin-bottom: 20px;
}
.info-group {
  margin-bottom: 10px;
}
.totais {
  margin-top: 15px;
}

.card {
max-width: 90%;
margin: 0 auto;
}

.form-header {
display: flex;
justify-content: space-between;
align-items: center;
margin-bottom: 0rem;
}

.form-header h4 {
margin: 0;
font-weight: bold;
text-align: left;
}

.form-content {
margin-top: 1.5rem;
}

button.btn-sm {
padding: 6px 12px;
}

/* Ícone de download preto */
.btn-link i.bi-download {
  font-size: 1rem;  /* Ajuste o tamanho do ícone */
  color: black !important; /* Mantém preto */
}

/* Centralizar ícone na coluna */
td.text-center {
  text-align: center;
  vertical-align: middle;
}

</style>